import { Analytics } from '@util/Analytics';
import { Environment } from '@util/Environment';
import firebase from 'firebase/compat/app';
import 'firebase/compat/remote-config';
import { ActiveExperimentList, AllExperiments } from './AllExperiments';
import { MarkViewedOn, VariantsConfig } from './Experiment';
import { ExperimentStorage } from './ExperimentStorage';
import { RemoteConfigExperiment } from './RemoteConfigExperiment';

const FOUR_SECS = 4000;
const TEN_SECS = 10000;
const ONE_HOUR = 3600000;

const ActiveRemoteConfigExperimentList = ActiveExperimentList.filter(
  (experiment) => experiment instanceof RemoteConfigExperiment
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) as RemoteConfigExperiment<any>[];

/** Whether startup must wait for RemoteConfig before the first screen. The Dev example changes nothing users see. */
const isRemoteExperimentActive = ActiveRemoteConfigExperimentList.some(
  (experiment) => experiment !== AllExperiments.Dev
);

const remoteConfig = firebase.remoteConfig;

/**
 * Initialize experiments based on Firebase RemoteConfig. This is asynchronous so should be called as early as
 * possible during app startup to give the best possible chance of the experiments being available before screens that
 * use them are rendered.
 *
 * `localStorage` overrides are applied synchronously, before the first `await`, so they are in place even when the
 * caller doesn't wait for the fetch.
 */
async function initialize(): Promise<void> {
  try {
    ExperimentStorage.load(); // Loads experiment overrides

    const rc = remoteConfig();

    setupRemoteConfig(rc);
    await activateRemoteConfigVariants(rc); // Activates the previously stored values or the default values

    await rc.fetch();
    await activateRemoteConfigVariants(rc); // Activates the newly fetched values
  } catch (error) {
    const firebaseError = error as firebase.FirebaseError;
    Analytics.track({
      event: Analytics.Event.e_remote_config_error,
      code: firebaseError.code,
      message: firebaseError.message,
    });
    throw error;
  } finally {
    // Now that RemoteConfig is initialized, the variants shouldn't change again during startup
    markExperimentsViewed();
  }
}

/** Configure RemoteConfig settings and default values. */
function setupRemoteConfig(rc: firebase.remoteConfig.RemoteConfig) {
  rc.settings.minimumFetchIntervalMillis = Environment.DEVELOPMENT ? TEN_SECS : ONE_HOUR;

  rc.settings.fetchTimeoutMillis = FOUR_SECS;

  rc.defaultConfig = ActiveRemoteConfigExperimentList.reduce<VariantsConfig>(
    (config, experiment) => ({
      ...config,
      [experiment.configKey]: experiment.defaultVariant,
    }),
    {}
  );
}

/** Activate RemoteConfig values changes, and notify anyone listening, e.g. React components. */
async function activateRemoteConfigVariants(rc: firebase.remoteConfig.RemoteConfig) {
  const changesMade = await rc.activate();

  if (changesMade === true) {
    // Notify anyone listening that the experiment values have changed
    ActiveRemoteConfigExperimentList.forEach((experiment) => experiment.onChange.dispatch());
  }
}

function markExperimentsViewed() {
  ActiveExperimentList.filter(
    (experiment) => experiment.markViewedOn === MarkViewedOn.Init
  ).forEach((experiment) => experiment.viewed());
}

export const Experiments = {
  initialize,
  isRemoteExperimentActive,
  ...AllExperiments,
};
